import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Building2, TrendingUp, DollarSign, Wrench, FastForward, ShoppingCart, UserCheck, Search, RefreshCcw, Megaphone, Wallet, Activity } from "lucide-react";
import { SimState, OwnedProperty, fmt } from "@/hooks/useREInvestingSim";
import MarketIndicator from "./MarketIndicator";
import { getPropertyImage } from "./propertyImages";
import { getRealm } from "@/components/lunimar/lunimarRealms";
import { RiskMeter, type RiskLevel } from "@/components/lunimar-kit";

interface Props {
  sim: SimState;
  totalPortfolioValue: number;
  totalMortgageDebt: number;
  totalEquity: number;
  netWorth: number;
  monthlyCashFlow: number;
  onAdvance: () => void;
  onPropertyAction: (propertyKey: string, action: string) => void;
  onBuyMore: () => void;
  processing: boolean;
}

function PropertyCard({ prop, onAction, processing }: { prop: OwnedProperty; onAction: (action: string) => void; processing: boolean }) {
  const realm = getRealm("re_investing");
  const equity = prop.current_value - prop.mortgage_balance;
  const cashFlow = prop.occupied
    ? prop.current_rent - prop.monthly_mortgage - prop.hoa_monthly - (prop.has_property_manager ? Math.round(prop.current_rent * (prop.manager_fee_pct || 0.08)) : 0)
    : -prop.monthly_mortgage - prop.hoa_monthly;

  const img = getPropertyImage(prop.property_key);

  return (
    <div
      className="overflow-hidden rounded-2xl border backdrop-blur-md"
      style={{
        borderColor: "hsl(0 0% 100% / 0.10)",
        background: "hsl(228 45% 12% / 0.55)",
      }}
    >
      {img && (
        <div className="w-full h-28 overflow-hidden">
          <img src={img} alt={prop.property_name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4" style={{ color: `hsl(${realm.primaryAccent})` }} />
          <div>
            <p className="font-heading text-sm font-semibold text-white">{prop.property_name}</p>
            <p className="text-[10px] font-mono uppercase tracking-wider text-white/55">
              {prop.city} • {prop.property_type}
              {prop.has_property_manager && <span className="ml-1" style={{ color: `hsl(${realm.secondaryAccent})` }}>• Managed</span>}
            </p>
          </div>
        </div>
        <span
          className="rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider"
          style={{
            borderColor: prop.occupied ? "hsl(152 70% 55% / 0.5)" : "hsl(0 78% 60% / 0.5)",
            background: prop.occupied ? "hsl(152 70% 32% / 0.18)" : "hsl(0 78% 32% / 0.18)",
            color: prop.occupied ? "hsl(152 70% 60%)" : "hsl(0 78% 60%)",
          }}
        >
          {prop.occupied ? "Occupied" : `Vacant (${prop.vacancy_months}mo)`}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-1.5 text-center">
        {[
          { label: "Value",    value: fmt(prop.current_value),   hsl: realm.primaryAccent },
          { label: "Equity",   value: fmt(equity),               hsl: realm.secondaryAccent },
          { label: "Rent",     value: `${fmt(prop.current_rent)}/mo`, hsl: "152 70% 55%" },
          { label: "Cashflow", value: `${fmt(cashFlow)}/mo`,     hsl: cashFlow >= 0 ? "152 70% 55%" : "0 78% 60%" },
        ].map(s => (
          <div
            key={s.label}
            className="rounded-lg border p-1.5"
            style={{
              borderColor: `hsl(${s.hsl} / 0.25)`,
              background: `hsl(${s.hsl} / 0.08)`,
            }}
          >
            <p className="text-[9px] font-mono uppercase tracking-wider text-white/55">{s.label}</p>
            <p className="mt-0.5 font-heading text-[11px] font-bold tabular-nums" style={{ color: `hsl(${s.hsl})` }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Condition + tenant bars */}
      <div className="grid grid-cols-2 gap-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="space-y-1 cursor-help">
              <p className="text-[9px] text-muted-foreground">Condition ({prop.condition_score}/100)</p>
              <Progress value={prop.condition_score} className="h-1.5" />
            </div>
          </TooltipTrigger>
          <TooltipContent>Property condition affects repair frequency and tenant satisfaction. Use Repair or Renovate to improve.</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="space-y-1 cursor-help">
              <p className="text-[9px] text-muted-foreground">Tenant Quality ({prop.tenant_quality}/100)</p>
              <Progress value={prop.tenant_quality} className="h-1.5" />
            </div>
          </TooltipTrigger>
          <TooltipContent>Higher tenant quality means more reliable rent and less damage. Raising rent can lower this.</TooltipContent>
        </Tooltip>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-1.5">
        {[
          { action: "repair", label: "Repair", icon: Wrench, disabled: false, tip: "Fix issues, improve condition ($500-$2,500)" },
          { action: "renovate", label: "Renovate", icon: TrendingUp, disabled: false, tip: "Major upgrade: boost value +8%, rent +5% ($3k-$8k)" },
          { action: "inspect", label: "Inspect", icon: Search, disabled: false, tip: "Get detailed report on property condition ($250)" },
          { action: "raise_rent", label: "↑ Rent", icon: null, disabled: !prop.occupied, tip: "Raise rent 5% — may reduce tenant quality" },
          { action: "lower_rent", label: "↓ Rent", icon: null, disabled: false, tip: "Lower rent 5% — can help fill vacancy" },
          { action: "hire_manager", label: prop.has_property_manager ? "Self-Manage" : "Hire Manager", icon: UserCheck, disabled: false, tip: prop.has_property_manager ? "Switch to self-management" : "Hire a property manager (8% of rent)" },
          { action: "refinance", label: "Refinance", icon: RefreshCcw, disabled: prop.mortgage_balance <= 0, tip: "Refinance at lower rate (1.5% fee)" },
          { action: "market_tenant", label: "Market", icon: Megaphone, disabled: prop.occupied, tip: "Actively market for new tenant ($500, 70% success rate)" },
          { action: "sell", label: "Sell", icon: null, disabled: false, tip: "Sell property (6% selling costs)" },
        ].map(btn => (
          <Tooltip key={btn.action}>
            <TooltipTrigger asChild>
              <Button
                variant={btn.action === "sell" ? "destructive" : "outline"}
                size="sm"
                disabled={processing || btn.disabled}
                onClick={() => onAction(btn.action)}
                className="text-[10px] h-7 px-2 gap-1"
              >
                {btn.icon && <btn.icon className="w-3 h-3" />}
                {btn.label}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="text-xs max-w-xs">{btn.tip}</TooltipContent>
          </Tooltip>
        ))}
      </div>
      </div>
    </div>
  );
}

export default function PortfolioDashboard({ sim, totalPortfolioValue, totalMortgageDebt, totalEquity, netWorth, monthlyCashFlow, onAdvance, onPropertyAction, onBuyMore, processing }: Props) {
  const realm = getRealm("re_investing");
  const leverage = totalPortfolioValue > 0 ? totalMortgageDebt / totalPortfolioValue : 0;
  const leverageRisk: RiskLevel =
    leverage > 0.85 ? "Severe" : leverage > 0.7 ? "High" : leverage > 0.5 ? "Medium" : "Low";

  return (
    <div className="space-y-6">
      {/* Header stats */}
      <div className="flex items-center justify-between">
        <div>
          <div
            className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
            style={{ color: `hsl(${realm.primaryAccent})` }}
          >
            <Activity className="h-3 w-3" /> Skyline Holdings · Command deck
          </div>
          <h2 className="font-heading text-lg font-bold text-white">Portfolio Dashboard</h2>
          <p className="font-mono text-[11px] uppercase tracking-wider text-white/55">
            Round {sim.current_round} / {sim.max_rounds}
          </p>
        </div>
        <MarketIndicator cycle={sim.market_cycle} cycleHistory={sim.metrics?.cycle_history} />
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          { label: "Cash",            value: fmt(sim.available_cash),    icon: Wallet,     hsl: realm.primaryAccent,   tip: "Your available cash for purchases, repairs, and reserves" },
          { label: "Portfolio value", value: fmt(totalPortfolioValue),   icon: Building2,  hsl: realm.secondaryAccent, tip: "Total estimated value of all owned properties" },
          { label: "Net worth",       value: fmt(netWorth),              icon: TrendingUp, hsl: "152 70% 55%",         tip: "Cash + equity (property values minus mortgages)" },
          { label: "Monthly cashflow",value: fmt(monthlyCashFlow),       icon: DollarSign, hsl: monthlyCashFlow >= 0 ? "152 70% 55%" : "0 78% 60%", tip: "Net monthly income from all properties after expenses" },
        ].map(stat => {
          const Icon = stat.icon;
          return (
            <Tooltip key={stat.label}>
              <TooltipTrigger asChild>
                <div
                  className="cursor-help rounded-xl border p-3 backdrop-blur-md"
                  style={{
                    borderColor: `hsl(${stat.hsl} / 0.25)`,
                    background: `hsl(${stat.hsl} / 0.08)`,
                  }}
                >
                  <div className="mb-1 flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5" style={{ color: `hsl(${stat.hsl})` }} />
                    <p className="text-[10px] font-mono uppercase tracking-wider text-white/60">{stat.label}</p>
                  </div>
                  <p className="font-heading text-sm font-bold tabular-nums" style={{ color: `hsl(${stat.hsl})` }}>{stat.value}</p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-xs">{stat.tip}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      {/* Leverage risk meter */}
      {totalPortfolioValue > 0 && (
        <RiskMeter
          level={leverageRisk}
          label="Portfolio leverage"
          hint={`Debt covers ${(leverage * 100).toFixed(0)}% of portfolio value. Higher leverage = less room to absorb a vacancy or repair shock.`}
        />
      )}

      {/* Achievements */}
      {sim.achievements.length > 0 && (
        <div
          className="rounded-xl border p-3 backdrop-blur-md"
          style={{
            borderColor: `hsl(${realm.tertiaryAccent} / 0.25)`,
            background: `hsl(${realm.tertiaryAccent} / 0.06)`,
          }}
        >
          <p className="mb-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: `hsl(${realm.tertiaryAccent})` }}>
            Achievements · {sim.achievements.length}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sim.achievements.map(a => (
              <span
                key={a}
                className="rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider"
                style={{
                  borderColor: `hsl(${realm.tertiaryAccent} / 0.4)`,
                  color: `hsl(${realm.tertiaryAccent})`,
                }}
              >
                {a.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Properties */}
      <div className="space-y-3">
        <h3 className="font-heading text-sm font-semibold text-white">Your properties · {sim.portfolio.length}</h3>
        {sim.portfolio.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur-md">
            <p className="text-sm text-white/70">No properties yet. Buy your first investment property to start operations.</p>
          </div>
        )}
        {sim.portfolio.map(prop => (
          <PropertyCard
            key={prop.property_key}
            prop={prop}
            onAction={(action) => onPropertyAction(prop.property_key, action)}
            processing={processing}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={onAdvance} disabled={processing} className="flex-1 gap-2">
          <FastForward className="w-4 h-4" />
          {processing ? "Processing..." : "Advance Month"}
        </Button>
        <Button variant="outline" onClick={onBuyMore} disabled={processing} className="gap-2">
          <ShoppingCart className="w-4 h-4" />
          Buy More
        </Button>
      </div>

      {/* Progress */}
      <div className="space-y-1">
        <div className="flex justify-between font-mono text-[11px] uppercase tracking-wider text-white/55">
          <span>Simulation progress</span>
          <span>{Math.round((sim.current_round / sim.max_rounds) * 100)}%</span>
        </div>
        <Progress value={(sim.current_round / sim.max_rounds) * 100} className="h-2" />
      </div>
    </div>
  );
}
